/**
 * Runtime configuration contract for the internal Halo UI.
 *
 * Values are read synchronously from the `#app-config` data block that ships inside
 * `index.html` — filled at build time from `.env.*` for local and legacy builds, and
 * rewritten at deploy time from the platform manifest for harbor targets. Reading from the
 * document means configuration exists before any module executes, so the accessor is
 * non-optional and there is no bootstrap ordering contract for consumers to honor.
 *
 * Only public values ever appear here — never secrets.
 */
export interface RuntimeConfig {
    PLATFORM_ROOT_DOMAIN: string
    PUB_PLAT_URL: string
    INT_PLAT_URL: string
    PUB_API_URL: string
    INT_API_URL: string
    INSIGHTS_URL: string
    CDN_URL: string
    MEDIA_URL: string
    PLATFORM_DOMAIN_KEY: string
    /**
     * The Auth0 tenant Halo authenticates against, and its public SPA client id.
     *
     * Halo is single-tenant per environment: it manages whitelabels but does not resolve its
     * own identity from them, so unlike pufferfish it has no per-hostname settings lookup to
     * read these from. They are required, because an absent value would silently point the
     * login flow at the wrong tenant rather than fail. The identity host is not carried here —
     * it is derived from `PLATFORM_ROOT_DOMAIN`.
     */
    AUTH0_TENANT_ID: string
    AUTH0_CLIENT_ID: string
    /**
     * Serves the maintenance page instead of the app when it holds `MAINT_MODE_ON`.
     *
     * Optional, and any other value — absent, blank, or a stale hand-edit — leaves the app
     * serving normally. An operator sets it on an already-deployed `index.html`, so it must
     * never be a required key: a block that predates it, or one an operator has finished
     * with, has to boot.
     */
    MAINT_MODE?: string
}

/**
 * The declared allowlist, in declared order, mirroring the app's `config` mapping in the
 * platform's frontend-apps declaration and the build-time serializer in
 * `config/app-config.js`.
 *
 * Validating against this list is load-bearing rather than defensive: a block that was
 * never handed its configuration must fail at boot instead of leaving the app to run
 * against undefined endpoints.
 *
 * `MAINT_MODE` is declared in the build-time allowlist but is deliberately absent here: it
 * is an operator lever with a safe default, and requiring it would make an incident switch
 * able to take the app down by being missing.
 */
export const REQUIRED_CONFIG_KEYS: Array<keyof RuntimeConfig> = [
    'PLATFORM_ROOT_DOMAIN',
    'PUB_PLAT_URL',
    'INT_PLAT_URL',
    'PUB_API_URL',
    'INT_API_URL',
    'INSIGHTS_URL',
    'CDN_URL',
    'MEDIA_URL',
    'PLATFORM_DOMAIN_KEY',
    'AUTH0_TENANT_ID',
    'AUTH0_CLIENT_ID',
]

/** The one `MAINT_MODE` value that serves the maintenance page. */
export const MAINT_MODE_ON = 'on'

const APP_CONFIG_ELEMENT_ID = 'app-config'

/**
 * Reads and validates the configuration block from the current document.
 *
 * A key present with an empty string is accepted: an environment may legitimately resolve a
 * declared value to blank. A key that is absent entirely is not, because that is what an
 * unfilled block looks like.
 */
export function readAppConfig(): RuntimeConfig {
    const el = document.getElementById(APP_CONFIG_ELEMENT_ID)
    if (!el) {
        throw new Error(`Missing #${APP_CONFIG_ELEMENT_ID} block in index.html`)
    }

    const contents = el.textContent?.trim()
    if (!contents) {
        throw new Error(`Empty #${APP_CONFIG_ELEMENT_ID} block in index.html`)
    }

    let parsed: unknown
    try {
        parsed = JSON.parse(contents)
    } catch (err) {
        throw new Error(`Unparseable #${APP_CONFIG_ELEMENT_ID} block in index.html: ${(err as Error).message}`)
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error(`Invalid #${APP_CONFIG_ELEMENT_ID} block in index.html: expected a JSON object`)
    }

    const cfg = parsed as Record<string, unknown>
    const missing = REQUIRED_CONFIG_KEYS.filter((key) => typeof cfg[key] !== 'string')
    if (missing.length > 0) {
        throw new Error(`Incomplete #${APP_CONFIG_ELEMENT_ID} block in index.html: missing ${missing.join(', ')}`)
    }

    return cfg as unknown as RuntimeConfig
}

const runtimeConfig: RuntimeConfig = readAppConfig()

/**
 * Returns the runtime configuration read from the document at module load.
 */
export function getRuntimeConfig(): RuntimeConfig {
    return runtimeConfig
}

/**
 * Whether the app should serve the maintenance page instead of itself.
 *
 * An operator sets this by hand on a live target, so the value is trimmed before comparison
 * and only the exact enabling value counts. An absent or blank key is off, which is what
 * makes the lever safe to leave declared blank in every committed environment file.
 */
export function isMaintenanceMode(): boolean {
    return runtimeConfig.MAINT_MODE?.trim() === MAINT_MODE_ON
}
